import { lumi } from '@/lib/lumi'
import jsPDF from 'jspdf'
import { Estimate, CalculatorState, CountryConfig, MaterialOption } from './types'
import { maintenanceCosts30Years } from './constants'

// Generate chart image for maintenance costs
const generateMaintenanceChart = async (
  state: CalculatorState,
  currentCountry: CountryConfig,
  materialOptions: MaterialOption[]
): Promise<string> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    canvas.width = 800
    canvas.height = 400
    const ctx = canvas.getContext('2d')
    
    if (!ctx) {
      resolve('')
      return
    }
    
    // Background
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    
    // Chart setup
    const topMaterials = ['asphalt', 'metal', 'tile', 'slate', 'solar_tiles', 'green_roof']
    const barWidth = 100
    const barSpacing = 30
    const maxHeight = 300
    const startX = 50
    const startY = 350
    
    // Find max value for scaling
    const maxValue = Math.max(
      ...topMaterials.map((mat) => {
        const maintenance = maintenanceCosts30Years[mat]
        const installCost = ((materialOptions.find(m => m.value === mat)?.low || 0) + 
                            (materialOptions.find(m => m.value === mat)?.high || 0)) / 2 * parseInt(state.squareFootage)
        return installCost + (maintenance?.total || 0) * (parseInt(state.squareFootage) / 100)
      })
    )
    
    // Draw bars
    topMaterials.forEach((mat, idx) => {
      const materialLabel = materialOptions.find(m => m.value === mat)?.label || mat
      const maintenance = maintenanceCosts30Years[mat]
      const installCost = ((materialOptions.find(m => m.value === mat)?.low || 0) + 
                          (materialOptions.find(m => m.value === mat)?.high || 0)) / 2 * parseInt(state.squareFootage)
      const maintCost = (maintenance?.total || 0) * (parseInt(state.squareFootage) / 100)
      const totalCost = installCost + maintCost
      
      const barHeight = (totalCost / maxValue) * maxHeight
      const x = startX + idx * (barWidth + barSpacing)
      
      // Installation cost (blue)
      const installHeight = (installCost / totalCost) * barHeight
      ctx.fillStyle = '#1E40AF'
      ctx.fillRect(x, startY - barHeight, barWidth, installHeight)
      
      // Maintenance cost (orange)
      ctx.fillStyle = '#F97316'
      ctx.fillRect(x, startY - barHeight + installHeight, barWidth, barHeight - installHeight)
      
      // Border
      ctx.strokeStyle = '#333333'
      ctx.lineWidth = 1
      ctx.strokeRect(x, startY - barHeight, barWidth, barHeight)
      
      // Label
      ctx.fillStyle = '#000000'
      ctx.font = '12px Arial'
      ctx.textAlign = 'center'
      const shortLabel = materialLabel.substring(0, 12)
      ctx.fillText(shortLabel, x + barWidth / 2, startY + 15)
      
      // Value
      ctx.font = 'bold 10px Arial'
      ctx.fillText(`${currentCountry.currencySymbol}${Math.round(totalCost / 1000)}k`, x + barWidth / 2, startY - barHeight - 5)
    })
    
    // Legend
    ctx.fillStyle = '#1E40AF'
    ctx.fillRect(20, 20, 20, 20)
    ctx.fillStyle = '#000000'
    ctx.font = '12px Arial'
    ctx.textAlign = 'left'
    ctx.fillText('Installation Cost', 45, 35)
    
    ctx.fillStyle = '#F97316'
    ctx.fillRect(180, 20, 20, 20)
    ctx.fillStyle = '#000000'
    ctx.fillText('30-Year Maintenance', 205, 35)
    
    // Title
    ctx.font = 'bold 16px Arial'
    ctx.textAlign = 'center'
    ctx.fillText('Total Cost of Ownership (30 Years)', canvas.width / 2, 60)
    
    resolve(canvas.toDataURL('image/png'))
  })
}

export const generatePDF = async (
  estimate: Estimate,
  state: CalculatorState,
  currentCountry: CountryConfig,
  materialOptions: MaterialOption[]
) => {
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  
  // Header
  doc.setFillColor(30, 64, 175)
  doc.rect(0, 0, pageWidth, 40, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(24)
  doc.text('Roof Cost Estimate', pageWidth / 2, 25, { align: 'center' })
  
  doc.setTextColor(0, 0, 0)
  
  // Project Details
  doc.setFontSize(16)
  doc.text('Project Details', 20, 55)
  doc.setFontSize(11)
  doc.text(`Zip Code: ${state.zipCode}`, 20, 65)
  doc.text(`Square Footage: ${state.squareFootage} sq ft`, 20, 72)
  doc.text(`Material: ${materialOptions.find(m => m.value === state.material)?.label || state.material}`, 20, 79)
  doc.text(`Remove Old Roof: ${state.removeOldRoof === 'yes' ? 'Yes' : 'No'}`, 20, 86)
  if (state.removeOldRoof === 'yes') {
    doc.text(`Existing Layers: ${state.numLayers}`, 20, 93)
  }
  
  // Property Characteristics
  doc.setFontSize(16)
  doc.text('Property Characteristics', 20, 110)
  doc.setFontSize(11)
  let yPos = 120
  doc.text(`Stories: ${state.numStories}`, 20, yPos)
  yPos += 7
  doc.text(`Roof Pitch: ${state.roofPitch}`, 20, yPos)
  yPos += 7
  doc.text(`Roof Condition: ${state.roofCondition}`, 20, yPos)
  yPos += 7
  doc.text(`Chimneys: ${state.hasChimneys}`, 20, yPos)
  yPos += 7
  doc.text(`Skylights: ${state.hasSkylights}`, 20, yPos)
  yPos += 7
  doc.text(`Complexity: ${state.hasValleys} valleys`, 20, yPos)
  yPos += 7
  doc.text(`Timeline: ${state.urgency}`, 20, yPos)
  
  // Cost Breakdown
  doc.setFontSize(16)
  doc.text('Cost Breakdown', 20, yPos + 15)
  doc.setFontSize(11)
  yPos += 25
  doc.text('Base Material & Installation: Included', 20, yPos)
  
  if (state.removeOldRoof === 'yes' && estimate.breakdown.tearOff > 0) {
    yPos += 7
    doc.text(`Tear Off (${state.numLayers} layer${state.numLayers > 1 ? 's' : ''}): +$${estimate.breakdown.tearOff.toLocaleString()}`, 20, yPos)
  }
  if (state.roofPitch !== 'low') {
    yPos += 7
    doc.text(`Pitch Adjustment (${state.roofPitch}): +${((estimate.breakdown.pitchMultiplier - 1) * 100).toFixed(0)}%`, 20, yPos)
  }
  if (state.numStories > 1) {
    yPos += 7
    doc.text(`Multi-Story Labor (${state.numStories} stories): +${((estimate.breakdown.storiesMultiplier - 1) * 100).toFixed(0)}%`, 20, yPos)
  }
  if (state.hasChimneys > 0) {
    yPos += 7
    doc.text(`Chimney Flashing (${state.hasChimneys}): +$${estimate.breakdown.chimneyFlashing.toLocaleString()}`, 20, yPos)
  }
  if (state.hasSkylights > 0) {
    yPos += 7
    doc.text(`Skylight Flashing (${state.hasSkylights}): +$${estimate.breakdown.skylightFlashing.toLocaleString()}`, 20, yPos)
  }
  if (estimate.breakdown.complexityFee > 0) {
    yPos += 7
    doc.text(`Complexity Fee (${state.hasValleys} valleys): +$${estimate.breakdown.complexityFee.toLocaleString()}`, 20, yPos)
  }
  if (estimate.breakdown.urgencyFee > 0) {
    yPos += 7
    doc.text(`Urgency Premium (${state.urgency}): +$${estimate.breakdown.urgencyFee.toLocaleString()}`, 20, yPos)
  }
  
  // Total Estimate
  yPos += 15
  doc.setFillColor(249, 115, 22)
  doc.rect(15, yPos - 5, pageWidth - 30, 20, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(16)
  doc.text('TOTAL ESTIMATED COST:', 20, yPos + 5)
  doc.setFontSize(18)
  doc.text(`$${estimate.low.toLocaleString()} - $${estimate.high.toLocaleString()}`, pageWidth - 20, yPos + 5, { align: 'right' })
  
  // New page for maintenance comparison
  doc.addPage()
  yPos = 20
  
  doc.setTextColor(0, 0, 0)
  doc.setFontSize(16)
  doc.text('30-Year Total Cost of Ownership', 20, yPos)
  yPos += 10
  
  // Generate and add chart
  const chartImage = await generateMaintenanceChart(state, currentCountry, materialOptions)
  if (chartImage) {
    doc.addImage(chartImage, 'PNG', 15, yPos, 180, 90)
    yPos += 100
  }
  
  // Maintenance table
  doc.setFontSize(14)
  doc.text('Detailed Maintenance Breakdown', 20, yPos)
  doc.setFontSize(9)
  yPos += 10
  
  // Table header
  doc.setFillColor(30, 64, 175)
  doc.rect(15, yPos - 5, pageWidth - 30, 10, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(9)
  doc.text('Material', 20, yPos)
  doc.text('Install', 75, yPos)
  doc.text('Maint.', 105, yPos)
  doc.text('Total', 135, yPos)
  doc.text('Lifespan', 165, yPos)
  yPos += 10
  
  // Table rows for top materials
  const topMaterials = ['asphalt', 'metal', 'tile', 'slate', 'solar_tiles', 'green_roof']
  doc.setTextColor(0, 0, 0)
  
  topMaterials.forEach((mat, idx) => {
    const materialLabel = materialOptions.find(m => m.value === mat)?.label || mat
    const maintenance = maintenanceCosts30Years[mat]
    const installCost = estimate.material === mat ? estimate.average : 
      ((materialOptions.find(m => m.value === mat)?.low || 0) + (materialOptions.find(m => m.value === mat)?.high || 0)) / 2 * parseInt(state.squareFootage)
    const totalCost = installCost + (maintenance?.total || 0) * (parseInt(state.squareFootage) / 100)
    const lifespan = materialOptions.find(m => m.value === mat)?.lifespan || 'N/A'
    
    if (idx % 2 === 0) {
      doc.setFillColor(240, 240, 240)
      doc.rect(15, yPos - 5, pageWidth - 30, 10, 'F')
    }
    
    doc.setFontSize(8)
    doc.text(materialLabel.substring(0, 20), 20, yPos)
    doc.text(`${currentCountry.currencySymbol}${Math.round(installCost).toLocaleString()}`, 75, yPos)
    doc.text(`${currentCountry.currencySymbol}${Math.round((maintenance?.total || 0) * (parseInt(state.squareFootage) / 100)).toLocaleString()}`, 105, yPos)
    doc.text(`${currentCountry.currencySymbol}${Math.round(totalCost).toLocaleString()}`, 135, yPos)
    doc.text(lifespan, 165, yPos)
    yPos += 10
  })
  
  // Footer disclaimer
  yPos += 10
  doc.setTextColor(100, 100, 100)
  doc.setFontSize(9)
  const disclaimer = 'This estimate is based on industry averages and provided information. Actual costs may vary based on location, contractor rates, permits, and unforeseen conditions. For accurate pricing, consult licensed roofing professionals.'
  const splitDisclaimer = doc.splitTextToSize(disclaimer, pageWidth - 40)
  doc.text(splitDisclaimer, 20, yPos)
  
  doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 20, 280)
  doc.text('www.roofingcostscalculator.com', pageWidth - 20, 280, { align: 'right' })
  
  return doc
}

export const downloadPDF = async (
  estimate: Estimate,
  state: CalculatorState,
  currentCountry: CountryConfig,
  materialOptions: MaterialOption[]
) => {
  const doc = await generatePDF(estimate, state, currentCountry, materialOptions)
  doc.save(`Roof_Estimate_${state.zipCode}_${Date.now()}.pdf`)
}

export const sharePDFViaEmail = async (
  estimate: Estimate,
  state: CalculatorState,
  currentCountry: CountryConfig,
  materialOptions: MaterialOption[],
  recipientEmail: string
) => {
  try {
    // Generate PDF as base64
    const doc = await generatePDF(estimate, state, currentCountry, materialOptions)
    const pdfBlob = doc.output('blob')
    
    // Convert blob to base64
    const base64PDF = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1]
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(pdfBlob)
    })
    
    // Create email HTML with estimate summary
    const emailHTML = `
      <html>
        <body style="font-family: Arial, sans-serif; color: #333; max-width: 600px; margin: 0 auto;">
          <div style="background-color: #1E40AF; color: white; padding: 20px; text-align: center;">
            <h1 style="margin: 0;">Your Roof Cost Estimate</h1>
          </div>
          <div style="padding: 20px;">
            <h2 style="color: #1E40AF;">Project Summary</h2>
            <p><strong>Zip Code:</strong> ${state.zipCode}</p>
            <p><strong>Square Footage:</strong> ${state.squareFootage} sq ft</p>
            <p><strong>Material:</strong> ${materialOptions.find(m => m.value === state.material)?.label || state.material}</p>
            
            <div style="background-color: #FFF4E6; border-left: 4px solid #F97316; padding: 15px; margin: 20px 0;">
              <h3 style="margin-top: 0; color: #F97316;">Estimated Cost Range</h3>
              <p style="font-size: 24px; font-weight: bold; margin: 10px 0;">
                ${currentCountry.currencySymbol}${estimate.low.toLocaleString()} - ${currentCountry.currencySymbol}${estimate.high.toLocaleString()}
              </p>
            </div>
            
            <p>Your complete estimate is attached as a PDF, including detailed cost breakdown and 30-year maintenance comparison.</p>
            
            <p style="color: #666; font-size: 12px; margin-top: 30px;">
              This estimate is based on industry averages. Actual costs may vary. For accurate pricing, consult licensed roofing professionals.
            </p>
            
            <hr style="border: none; border-top: 1px solid #ddd; margin: 20px 0;" />
            <p style="text-align: center; color: #999; font-size: 12px;">
              Generated by <a href="https://www.roofingcostscalculator.com" style="color: #1E40AF;">RoofingCostsCalculator.com</a>
            </p>
          </div>
        </body>
      </html>
    `
    
    // Send email with Lumi SDK
    await lumi.tools.email.send({
      to: recipientEmail,
      subject: `Your Roof Estimate - ${state.zipCode} (${currentCountry.currencySymbol}${estimate.average.toLocaleString()})`,
      fromName: 'Roofing Costs Calculator',
      html: emailHTML
    })
    
    return { success: true }
  } catch (error) {
    console.error('Email send error:', error)
    throw error
  }
}
